// Anexar bancos de questões a um caderno (sessão de IA) como CONTEXTO.
// [[project-caderno-bank-context]]
//
// Componentes:
//   - LinkedBanksStrip  → faixa visual abaixo do header do caderno mostrando
//                         chips dos bancos anexados + CTA pra anexar.
//   - AttachBanksModal  → modal de seleção (checkbox) sobre os bancos que o
//                         aluno tem acesso, com indicador "Já anexado".
//
// Clareza > densidade: o aluno SEMPRE vê o que está conectado, tem como
// remover individualmente, e o estado vazio também é explícito.
// [[feedback-user-clarity-context]]

import { useEffect, useState } from "preact/hooks";
import { Loader2, Library, Plus, X, BookOpen, Check } from "lucide-react";
import { api } from "./api";
import type { BankRow, LinkedBankSummary } from "./api";
import { openConfirm } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

interface LinkedBanksStripProps {
  sessionId: string;
  linkedBanks: LinkedBankSummary[];
  onChange: (next: LinkedBankSummary[]) => void;
}

export function LinkedBanksStrip({
  sessionId,
  linkedBanks,
  onChange
}: LinkedBanksStripProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function detach(bank: LinkedBankSummary) {
    const ok = await openConfirm({
      title: "Remover banco do contexto?",
      message: `A IA vai parar de usar "${bank.name}" como referência neste caderno. As mensagens anteriores ficam, mas as próximas respostas perdem esse contexto.`,
      confirmLabel: "Remover",
      cancelLabel: "Cancelar"
    });
    if (!ok) return;
    setBusyId(bank.id);
    try {
      const r = await api.detachAiStudyBank(sessionId, bank.id);
      onChange(r.linkedBanks);
    } catch (e) {
      alert(friendlyError(e));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <section
        aria-label="Bancos anexados como contexto da IA"
        class="rounded-md border border-violet-500/30 bg-violet-500/5 px-3 py-2.5"
      >
        <header class="mb-2 flex items-center gap-2">
          <Library className="size-4 text-violet-600 dark:text-violet-400" />
          <h2 class="text-[12px] font-bold uppercase tracking-wide text-violet-700 dark:text-violet-300">
            Contexto da IA
          </h2>
          <span class="rounded-full bg-violet-500/15 px-2 py-0.5 text-[10px] font-semibold text-violet-700 dark:text-violet-300">
            {linkedBanks.length === 0
              ? "Nenhum banco"
              : linkedBanks.length === 1
                ? "1 banco"
                : `${linkedBanks.length} bancos`}
          </span>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            class="ml-auto inline-flex items-center gap-1 rounded-md border border-violet-500/40 bg-white px-2.5 py-1 text-[11px] font-bold text-violet-700 hover:bg-violet-50 dark:bg-violet-950/40 dark:text-violet-300 dark:hover:bg-violet-950/60"
          >
            <Plus className="size-3" />
            {linkedBanks.length === 0 ? "Anexar banco" : "Gerenciar"}
          </button>
        </header>

        {linkedBanks.length === 0 ? (
          <p class="text-[11px] leading-snug text-violet-700/80 dark:text-violet-300/80">
            <strong>Nenhum banco anexado.</strong> A IA está conversando só
            sobre o tema do caderno. Anexe um banco de questões pra ela usar
            os enunciados e gabaritos como referência nas explicações.
          </p>
        ) : (
          <div class="flex flex-wrap gap-1.5">
            {linkedBanks.map((b) => (
              <span
                key={b.id}
                class="group inline-flex items-center gap-1.5 rounded-full border border-violet-500/40 bg-white px-2.5 py-1 text-[11px] font-semibold text-violet-800 shadow-sm dark:bg-violet-950/50 dark:text-violet-200"
              >
                <BookOpen className="size-3 text-violet-500" />
                <span class="max-w-[180px] truncate">{b.name}</span>
                <span class="rounded-full bg-violet-500/15 px-1.5 py-0 text-[10px] font-bold text-violet-700 dark:text-violet-300">
                  {b.questionCount}
                </span>
                <button
                  type="button"
                  onClick={() => void detach(b)}
                  disabled={busyId === b.id}
                  title="Remover banco do contexto"
                  class="ml-0.5 -mr-1 rounded-full p-0.5 text-violet-500 hover:bg-violet-500/20 hover:text-violet-800 disabled:opacity-50 dark:hover:text-violet-100"
                >
                  {busyId === b.id ? (
                    <Loader2 className="size-3 animate-spin" />
                  ) : (
                    <X className="size-3" />
                  )}
                </button>
              </span>
            ))}
          </div>
        )}
      </section>

      {modalOpen && (
        <AttachBanksModal
          sessionId={sessionId}
          currentlyLinked={linkedBanks}
          onClose={() => setModalOpen(false)}
          onSaved={(next) => {
            onChange(next);
            setModalOpen(false);
          }}
        />
      )}
    </>
  );
}

interface AttachBanksModalProps {
  sessionId: string;
  currentlyLinked: LinkedBankSummary[];
  onClose: () => void;
  onSaved: (next: LinkedBankSummary[]) => void;
}

function AttachBanksModal({
  sessionId,
  currentlyLinked,
  onClose,
  onSaved
}: AttachBanksModalProps) {
  const [banks, setBanks] = useState<BankRow[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(
    new Set(currentlyLinked.map((b) => b.id))
  );
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const r = await api.banks();
        setBanks(r.banks);
      } catch (e) {
        setLoadErr(friendlyError(e));
      }
    })();
  }, []);

  // ESC fecha. Click fora NÃO fecha (segue padrão de modais de gestão).
  // [[feedback-modal-no-click-outside-close]]
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const MAX_LINKED = 5;
  const filteredBanks = (banks ?? []).filter((b) => {
    if (!q.trim()) return true;
    return b.name.toLowerCase().includes(q.trim().toLowerCase());
  });

  function toggle(bankId: string) {
    const next = new Set(selected);
    if (next.has(bankId)) {
      next.delete(bankId);
    } else {
      if (next.size >= MAX_LINKED) {
        setSaveErr(`Máximo de ${MAX_LINKED} bancos como contexto. Remova um antes de adicionar outro.`);
        return;
      }
      next.add(bankId);
    }
    setSaveErr(null);
    setSelected(next);
  }

  async function save() {
    setSaving(true);
    setSaveErr(null);
    try {
      const ids = Array.from(selected);
      const r = await api.attachAiStudyBanks(sessionId, ids);
      onSaved(r.linkedBanks);
    } catch (e) {
      setSaveErr(friendlyError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      class="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="attach-banks-title"
    >
      <div class="relative flex max-h-[85vh] w-[min(640px,92vw)] flex-col rounded-xl border border-border bg-card shadow-2xl">
        <header class="flex items-start gap-3 border-b border-border px-5 py-3">
          <Library className="mt-0.5 size-5 text-violet-500" />
          <div class="min-w-0 flex-1">
            <h2 id="attach-banks-title" class="text-base font-bold">
              Anexar bancos como contexto
            </h2>
            <p class="mt-0.5 text-[11px] leading-snug text-muted-foreground">
              A IA vai usar os enunciados, alternativas, gabaritos e
              comentários dos bancos selecionados como referência neste
              caderno. Selecione até <strong>{MAX_LINKED}</strong>.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            class="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>

        <div class="border-b border-border px-5 py-2">
          <input
            type="text"
            value={q}
            onInput={(e) => setQ((e.target as HTMLInputElement).value)}
            placeholder="Buscar banco pelo nome…"
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>

        <div class="flex-1 overflow-y-auto px-3 py-2">
          {!banks && !loadErr && (
            <p class="flex items-center gap-2 px-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Carregando bancos disponíveis…
            </p>
          )}
          {loadErr && (
            <p class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {loadErr}
            </p>
          )}
          {banks && filteredBanks.length === 0 && (
            <p class="px-2 py-6 text-center text-sm text-muted-foreground">
              {q ? `Nenhum banco bate com "${q}".` : "Nenhum banco disponível pra você por enquanto."}
            </p>
          )}
          <ul class="space-y-1">
            {filteredBanks.map((b) => {
              const isSel = selected.has(b.id);
              const wasLinked = currentlyLinked.some((cb) => cb.id === b.id);
              return (
                <li key={b.id}>
                  <button
                    type="button"
                    onClick={() => toggle(b.id)}
                    class={`flex w-full items-center gap-3 rounded-md border px-3 py-2 text-left transition ${
                      isSel
                        ? "border-violet-500/50 bg-violet-500/10"
                        : "border-border bg-card hover:bg-accent"
                    }`}
                  >
                    <span
                      class={`flex size-5 shrink-0 items-center justify-center rounded border ${
                        isSel
                          ? "border-violet-500 bg-violet-500 text-white"
                          : "border-border bg-background"
                      }`}
                    >
                      {isSel && <Check className="size-3.5" />}
                    </span>
                    <div class="min-w-0 flex-1">
                      <p class="line-clamp-1 text-sm font-semibold">{b.name}</p>
                      <p class="line-clamp-1 text-[11px] text-muted-foreground">
                        {b.questionCount} {b.questionCount === 1 ? "questão" : "questões"}
                        {b.description ? ` · ${b.description}` : ""}
                      </p>
                    </div>
                    {wasLinked && !isSel && (
                      <span class="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold text-amber-700 dark:text-amber-300">
                        Será removido
                      </span>
                    )}
                    {wasLinked && isSel && (
                      <span class="rounded-full border border-violet-500/40 bg-violet-500/10 px-2 py-0.5 text-[10px] font-bold text-violet-700 dark:text-violet-300">
                        Anexado
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <footer class="flex items-center gap-2 border-t border-border px-5 py-3">
          <p class="flex-1 text-[11px] text-muted-foreground">
            <strong>{selected.size}</strong> de {MAX_LINKED} selecionados
          </p>
          {saveErr && (
            <p class="text-[11px] font-semibold text-destructive">{saveErr}</p>
          )}
          <button
            type="button"
            onClick={onClose}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving}
            class="inline-flex items-center gap-1 rounded-md bg-violet-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-violet-700 disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Check className="size-3.5" />
            )}
            Salvar contexto
          </button>
        </footer>
      </div>
    </div>
  );
}
